/* ============================================================
   LUMEN — theme.js
   Intro animation, scroll reveals, AJAX cart drawer,
   mobile nav, product gallery + variants, sticky buy bar.
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.Lumen || {};
  var S = CFG.strings || {};
  // Respect a visitor's reduced-motion preference on the live storefront, but
  // always animate inside the theme editor — otherwise a merchant whose OS has
  // animations switched off can never see the effects they're paying for.
  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches && !inDesignMode;

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  function money(cents) {
    try {
      return new Intl.NumberFormat(CFG.locale || 'en', {
        style: 'currency', currency: CFG.currency || 'USD'
      }).format(cents / 100);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  /* ---------- INTRO ANIMATION ---------- */
  (function intro() {
    var el = document.getElementById('intro');
    if (!el) return;

    var cfg = CFG.intro || {};
    var KEY = 'lumen_intro_seen';

    function dismiss() {
      el.classList.add('is-out');
      document.body.classList.remove('intro-lock');
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 700);
    }

    // Inside the Shopify theme editor the intro always plays, otherwise a
    // merchant previewing their own store would only ever see it once and
    // could never review changes to it.
    var inEditor = !!(window.Shopify && window.Shopify.designMode);

    // Should it run at all?
    var skip = false;
    if (!cfg.enabled) skip = true;
    if (cfg.frequency === 'home' && !cfg.isHome) skip = true;
    if (cfg.frequency === 'session' && !inEditor) {
      try { if (sessionStorage.getItem(KEY) === '1') skip = true; } catch (e) {}
    }

    if (skip) {
      el.parentNode && el.parentNode.removeChild(el);
      return;
    }

    if (cfg.frequency === 'session' && !inEditor) {
      try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
    }

    document.body.classList.add('intro-lock');
    // Reduced-motion visitors get a calm, still intro — but for the SAME
    // length of time. Cutting it to 700ms was making it flash past.
    var wait = parseInt(cfg.duration, 10) || 4500;
    setTimeout(dismiss, wait);
    // never trap the customer if something goes wrong
    el.addEventListener('click', dismiss);
    setTimeout(function () { if (document.getElementById('intro')) dismiss(); }, wait + 4000);
  })();

  /* ---------- SCROLL REVEALS ---------- */
  /* Bound per-root so it can be re-run on sections the theme editor swaps in.
     Without that, re-rendered content keeps `.reveal` (opacity: 0) with no
     observer watching it, and the section looks empty forever. */
  var revealIO = null;

  function bindReveals(root) {
    var scope = root || document;
    var nodes = $$('.reveal', scope);
    // the section wrapper itself can carry .reveal
    if (scope !== document && scope.classList && scope.classList.contains('reveal')) nodes.push(scope);
    if (!nodes.length) return;

    if (!CFG.animate || reduce || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('in'); });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { en.target.classList.add('in'); revealIO.unobserve(en.target); }
        });
      }, { threshold: .12, rootMargin: '0px 0px -50px 0px' });
    }
    nodes.forEach(function (n, i) {
      if (n.classList.contains('in')) return;
      n.style.transitionDelay = (Math.min(i, 6) * 0.06) + 's';
      revealIO.observe(n);
    });
  }

  bindReveals(document);

  /* ---------- IMAGE FADE / BLUR-UP ON SCROLL ----------
     Each content image starts soft + blurred and resolves as it scrolls into
     view — the "next/image" look. Waits for the pixels to actually decode so it
     sharpens from blur to crisp, never fades in from an empty box.
     Uses a keyframe (not a transition) so it never clobbers the hover-zoom
     transform already on the cards. Yields entirely to reduced-motion. */
  function bindImageReveals(root) {
    var main = root || document.getElementById('main');
    if (!main || reduce || !('IntersectionObserver' in window)) return;

    function skip(im) {
      return im.closest('[data-gallery]')        // PDP main gallery (own switching)
        || im.closest('.gallery__thumbs')
        || im.closest('.ba')                      // before/after needs both layers solid
        || im.classList.contains('pcard__alt')    // the hover-swap image
        || im.hasAttribute('data-no-reveal');
    }

    var imgs = $$('img', main).filter(function (im) { return !skip(im); });
    if (!imgs.length) return;

    var vh = window.innerHeight || document.documentElement.clientHeight;
    function inViewNow(im) { var r = im.getBoundingClientRect(); return r.top < vh && r.bottom > 0; }

    function reveal(im) { im.classList.add('is-in'); }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var im = en.target;
        io.unobserve(im);
        if (im.complete && im.naturalWidth > 0) { reveal(im); return; }
        im.addEventListener('load', function () { reveal(im); }, { once: true });
        im.addEventListener('error', function () { reveal(im); }, { once: true });
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -6% 0px' });

    imgs.forEach(function (im) {
      // already on screen at load → leave it be (no flash); only fade what you scroll to
      if (inViewNow(im)) return;
      im.classList.add('img-rv');
      io.observe(im);
    });
  }

  bindImageReveals(document.getElementById('main'));

  /* Theme editor: every settings change re-renders the section, so anything
     that animates in has to be re-bound against the fresh DOM. This is why
     changing an image used to blank the block out — the markup came back with
     .reveal still at opacity 0 and nothing left watching it. */
  document.addEventListener('shopify:section:load', function (e) {
    bindReveals(e.target);
    bindImageReveals(e.target);
  });

  /* ---------- STICKY / OVERLAY HEADER ---------- */
  (function header() {
    var h = $('.site-header');
    if (!h) return;
    // Runs for any sticky header now, not just the transparent one — the
    // stuck state also shrinks the bar and drops a shadow.
    if (!h.classList.contains('is-sticky') && !document.body.classList.contains('header-overlay')) return;
    var stuck = null;
    var onScroll = function () {
      var v = window.scrollY > 60;
      if (v === stuck) return;              // only touch the DOM when it flips
      stuck = v;
      h.classList.toggle('is-stuck', v);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  })();

  /* ---------- MOBILE NAV ---------- */
  (function nav() {
    var panel = $('.mobile-nav');
    if (!panel) return;
    function open(v) {
      panel.classList.toggle('is-open', v);
      document.body.style.overflow = v ? 'hidden' : '';
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-nav-open]')) open(true);
      if (e.target.closest('[data-nav-close]')) open(false);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') open(false); });
  })();

  /* ---------- HEADER DROPDOWNS ---------- */
  (function dropdowns() {
    var groups = $$('[data-dropdown]');
    if (!groups.length) return;

    function close(g) {
      g.classList.remove('is-open');
      var t = $('[data-dropdown-toggle]', g);
      if (t) t.setAttribute('aria-expanded', 'false');
    }
    function open(g) {
      groups.forEach(function (o) { if (o !== g) close(o); });
      g.classList.add('is-open');
      var t = $('[data-dropdown-toggle]', g);
      if (t) t.setAttribute('aria-expanded', 'true');
    }
    function closeAll() { groups.forEach(close); }

    groups.forEach(function (g) {
      var toggle = $('[data-dropdown-toggle]', g);
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        if (g.classList.contains('is-open')) { close(g); } else { open(g); }
      });

      // Hover only for pointer devices. On a touchscreen the emulated hover
      // fires alongside the click and the panel would open and shut instantly.
      if (window.matchMedia('(hover: hover)').matches) {
        g.addEventListener('mouseenter', function () { open(g); });
        g.addEventListener('mouseleave', function () { close(g); });
      }

      g.addEventListener('focusout', function (e) {
        if (!g.contains(e.relatedTarget)) close(g);
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-dropdown]')) closeAll();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
  })();

  /* ---------- TOASTS ---------- */
  function toast(msg, type) {
    var wrap = document.getElementById('toasts');
    if (!wrap) return;
    var t = document.createElement('div');
    t.className = 'toast' + (type === 'err' ? ' toast--err' : '');
    t.textContent = msg;
    wrap.appendChild(t);
    setTimeout(function () { t.remove(); }, 3200);
  }
  window.lumenToast = toast;

  /* ============================================================
     CART
     ============================================================ */

  var drawer = document.getElementById('cart-drawer');
  var overlay = document.getElementById('cart-overlay');

  function openDrawer(v) {
    if (!drawer) return;
    drawer.classList.toggle('is-open', v);
    drawer.setAttribute('aria-hidden', String(!v));
    if (overlay) {
      overlay.hidden = false;
      requestAnimationFrame(function () { overlay.classList.toggle('is-open', v); });
      if (!v) setTimeout(function () { overlay.hidden = true; }, 400);
    }
    document.body.style.overflow = v ? 'hidden' : '';
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openDrawer(true); }
    if (e.target.closest('[data-cart-close]')) openDrawer(false);
  });
  if (overlay) overlay.addEventListener('click', function () { openDrawer(false); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') openDrawer(false); });

  function getCart() { return fetch('/cart.js').then(function (r) { return r.json(); }); }

  function renderCart(cart) {
    if (!cart) return;

    $$('[data-cart-count]').forEach(function (n) {
      n.textContent = cart.item_count;
      n.hidden = cart.item_count === 0;
    });

    var dc = document.getElementById('drawer-count');
    if (dc) dc.textContent = cart.item_count ? '(' + cart.item_count + ')' : '';

    var empty = document.getElementById('drawer-empty');
    var items = document.getElementById('drawer-items');
    var foot = document.getElementById('drawer-foot');
    var total = document.getElementById('drawer-total');
    var has = cart.item_count > 0;

    if (empty) empty.style.display = has ? 'none' : 'flex';
    if (foot) foot.hidden = !has;
    if (total) total.textContent = money(cart.total_price);

    if (items) {
      items.hidden = !has;
      items.innerHTML = cart.items.map(function (it, i) {
        var v = (it.variant_title && it.variant_title !== 'Default Title')
          ? '<div class="ditem__variant">' + esc(it.variant_title) + '</div>' : '';
        return '<div class="ditem" data-line="' + (i + 1) + '">' +
          '<a class="ditem__img" href="' + esc(it.url) + '">' +
            (it.image ? '<img src="' + esc(it.image) + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<div class="ditem__main">' +
            '<a class="ditem__title" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' + v +
            '<div class="ditem__row">' +
              '<span class="qty">' +
                '<button type="button" data-line-qty="-1" aria-label="Decrease">' + ICON.minus + '</button>' +
                '<span class="q">' + it.quantity + '</span>' +
                '<button type="button" data-line-qty="1" aria-label="Increase">' + ICON.plus + '</button>' +
              '</span>' +
              '<span class="ditem__price">' + money(it.final_line_price) + '</span>' +
            '</div>' +
            '<button type="button" class="ditem__remove" data-line-remove>Remove</button>' +
          '</div>' +
        '</div>';
      }).join('');
    }

    // free shipping bar
    var wrap = document.getElementById('ship-wrap');
    if (wrap && CFG.freeShip && CFG.freeShipThreshold > 0) {
      var lbl = document.getElementById('ship-label');
      var fill = document.getElementById('ship-fill');
      wrap.hidden = !has;
      var left = CFG.freeShipThreshold - cart.total_price;
      if (left <= 0) {
        lbl.innerHTML = '<strong>' + esc(S.freeShipUnlocked || 'Free shipping unlocked') + '</strong>';
        fill.style.width = '100%';
      } else {
        lbl.innerHTML = esc((S.freeShipProgress || "You're __AMOUNT__ away from free shipping")).replace('__AMOUNT__', '<strong>' + money(left) + '</strong>');
        fill.style.width = Math.min(100, (cart.total_price / CFG.freeShipThreshold) * 100) + '%';
      }
    }

    renderUpsell(cart);
    syncGift(cart);
  }

  /* ---- cart-drawer upsell ---- */
  function renderUpsell(cart) {
    if (!CFG.features || !CFG.features.cartUpsell) return;
    var box = document.getElementById('drawer-upsell');
    var slot = document.getElementById('drawer-upsell-slot');
    var dataEl = document.querySelector('[data-upsell-json]');
    if (!box || !slot || !dataEl) return;
    var list = [];
    try { list = JSON.parse(dataEl.textContent); } catch (e) {}
    if (!cart.item_count) { box.hidden = true; return; }
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.handle] = true; });
    var pick = null;
    for (var i = 0; i < list.length; i++) {
      if (list[i].available && !inCart[list[i].handle] && (!CFG.gift || list[i].id !== CFG.gift.id)) { pick = list[i]; break; }
    }
    if (!pick) { box.hidden = true; return; }
    box.hidden = false;
    // pick.multi === has_only_default_variant: true means a single variant we can add straight away
    slot.innerHTML = '<div class="upsell">' +
      '<a class="upsell__img" href="' + esc(pick.url) + '">' + (pick.image ? '<img src="' + esc(pick.image) + '" alt="" loading="lazy">' : '') + '</a>' +
      '<div class="upsell__meta"><a class="upsell__title" href="' + esc(pick.url) + '">' + esc(pick.title) + '</a><span class="upsell__price">' + esc(pick.price) + '</span></div>' +
      (pick.multi
        ? '<button type="button" class="upsell__add" data-upsell-add="' + pick.id + '">' + esc(S.add || 'Add') + '</button>'
        : '<a class="upsell__add" href="' + esc(pick.url) + '">' + esc(S.options || 'Choose') + '</a>') +
      '</div>';
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest('[data-upsell-add]');
    if (a) { e.preventDefault(); addToCart(a.getAttribute('data-upsell-add'), 1, a); }
  });

  /* ---- free gift over a spend ----
     Adds/removes the gift automatically. Making it $0 needs a matching
     automatic discount in Shopify — the theme only manages the line. */
  var giftBusy = false;
  function syncGift(cart) {
    var g = CFG.gift;
    if (!g || !g.enabled || !g.id) return;

    var giftIn = null, eligible = 0;
    cart.items.forEach(function (it, i) {
      if (it.variant_id === g.id) giftIn = { line: i + 1 };
      else eligible += it.final_line_price;
    });
    var met = eligible >= g.threshold;

    var wrap = document.getElementById('gift-wrap');
    var label = document.getElementById('gift-label');
    if (wrap) {
      if (!cart.item_count) { wrap.hidden = true; }
      else {
        wrap.hidden = false;
        wrap.classList.toggle('is-unlocked', met);
        if (label) {
          if (met) label.textContent = g.unlocked || '';
          else label.innerHTML = esc((g.locked || 'Spend __AMOUNT__ more for a free gift')).replace('__AMOUNT__', '<strong>' + money(g.threshold - eligible) + '</strong>');
        }
      }
    }

    if (giftBusy) return;
    // earned but not in the cart → add it
    if (met && !giftIn && cart.item_count) {
      giftBusy = true;
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: g.id, quantity: 1 }] })
      }).then(getCart).then(function (c) { giftBusy = false; renderCart(c); }).catch(function () { giftBusy = false; });
    }
    // no longer earned (or cart effectively empty) → pull it back out
    else if (giftIn && (!met || eligible === 0)) {
      giftBusy = true;
      fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: giftIn.line, quantity: 0 })
      }).then(function (r) { return r.json(); }).then(function (c) { giftBusy = false; renderCart(c); }).catch(function () { giftBusy = false; });
    }
  }

  var ICON = {
    minus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
  };

  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

  /* `extra` is an optional array of additional line items — used by the card
     add-on, which is a separate variant so Shopify can charge for it. They go
     in one /cart/add.js call so the cart never shows a product without the
     card it was bought with. */
  function addToCart(id, qty, btn, extra) {
    if (btn) btn.classList.add('is-busy');
    var items = [{ id: id, quantity: qty || 1 }].concat(extra || []);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || d.message || 'Error'); });
        return r.json();
      })
      .then(getCart)
      .then(function (c) {
        renderCart(c);
        toast(S.added || 'Added to cart');
        if (CFG.cartDrawer) openDrawer(true);
      })
      .catch(function (err) { toast(err.message || S.error || 'Something went wrong', 'err'); })
      .then(function () { if (btn) btn.classList.remove('is-busy'); });
  }
  window.lumenAdd = addToCart;

  function changeLine(line, q) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: q })
    }).then(function (r) { return r.json(); }).then(renderCart);
  }

  // quick add + drawer line controls
  var busy = false;
  document.addEventListener('click', function (e) {
    var q = e.target.closest('[data-quick-add]');
    if (q) { e.preventDefault(); addToCart(q.getAttribute('data-quick-add'), 1, q); return; }

    var row = e.target.closest('.ditem');
    if (!row || busy) return;
    var line = parseInt(row.getAttribute('data-line'), 10);

    var rm = e.target.closest('[data-line-remove]');
    var ch = e.target.closest('[data-line-qty]');
    if (!rm && !ch) return;

    var cur = parseInt(row.querySelector('.q').textContent, 10) || 1;
    var next = rm ? 0 : Math.max(0, cur + parseInt(ch.getAttribute('data-line-qty'), 10));
    busy = true;
    changeLine(line, next).catch(function () { toast(S.error || 'Error', 'err'); })
      .then(function () { busy = false; });
  });

  /* ---------- card add-on ----------
     Shows the message box only once a paid tier is picked, and hands the
     chosen variant back to the form handler below. */
  document.addEventListener('change', function (e) {
    var radio = e.target.closest('[data-addons] input[name="addon-choice"]');
    if (!radio) return;
    var box = radio.closest('[data-addons]');
    var write = box.querySelector('[data-addon-write]');
    var hint = box.querySelector('[data-addon-hint]');

    box.querySelectorAll('.addons__opt').forEach(function (o) { o.classList.remove('is-on'); });
    var label = radio.closest('.addons__opt');
    if (label) label.classList.add('is-on');

    /* Express checkout skips the cart and submits only the main variant, so a
       chosen card would vanish between the button and the receipt. Hide it
       while an add-on is selected; the cart route carries both line items. */
    var form = radio.closest('form[data-product-form]');
    if (form) form.classList.toggle('has-addon', !!radio.value);

    if (write) {
      write.hidden = !radio.value;
      // "we'll write it" asks about the person, not for the finished sentence
      if (hint && radio.value) {
        hint.textContent = radio.getAttribute('data-writes') === 'us'
          ? (hint.getAttribute('data-us') || 'Tell us anything about them — we do the writing.')
          : (hint.getAttribute('data-you') || hint.textContent);
      }
      if (radio.value) { var ta = write.querySelector('[data-addon-msg]'); if (ta) ta.focus(); }
    }
  });

  document.addEventListener('input', function (e) {
    var ta = e.target.closest('[data-addon-msg]');
    if (!ta) return;
    var box = ta.closest('[data-addons]');
    var out = box && box.querySelector('[data-addon-count]');
    if (out) out.textContent = String(ta.value.length);
  });

  /* ---------- product add-ons ----------
     Ordinary catalogue products offered next to the main one. Keeps a running
     total, and hides express checkout the same way the card does — Buy Now
     submits only the main variant, so anything ticked would be lost. */
  document.addEventListener('change', function (e) {
    var check = e.target.closest('[data-pairs] [data-pair-check]');
    if (!check) return;

    var box = check.closest('[data-pairs]');
    var label = check.closest('.pairs__opt');
    if (label) label.classList.toggle('is-on', check.checked);

    var cents = 0, picked = 0;
    $$('[data-pair-check]', box).forEach(function (c) {
      if (c.checked) { cents += parseInt(c.getAttribute('data-price'), 10) || 0; picked++; }
    });

    var foot = box.querySelector('[data-pairs-foot]');
    var total = box.querySelector('[data-pairs-total]');
    if (foot) foot.hidden = picked === 0;
    if (total) total.textContent = money(cents);

    var form = check.closest('form[data-product-form]');
    if (form) form.classList.toggle('has-pairs', picked > 0);
  });

  // product form AJAX
  $$('form[data-product-form]').forEach(function (f) {
    f.addEventListener('submit', function (e) {
      e.preventDefault();
      var id = f.querySelector('[name="id"]').value;
      var qtyEl = f.querySelector('[name="quantity"]');

      var extra = [];
      var box = f.querySelector('[data-addons]');
      if (box) {
        var picked = box.querySelector('input[name="addon-choice"]:checked');
        if (picked && picked.value) {
          var ta = box.querySelector('[data-addon-msg]');
          var msg = ta ? ta.value.trim() : '';
          var props = {};
          if (msg) props[picked.getAttribute('data-writes') === 'us' ? 'About them' : 'Message'] = msg;
          extra.push({ id: picked.value, quantity: 1, properties: props });
        }
      }

      var pairs = f.querySelector('[data-pairs]');
      if (pairs) {
        $$('[data-pair-check]', pairs).forEach(function (c) {
          if (c.checked) extra.push({ id: c.value, quantity: 1 });
        });
      }

      addToCart(id, qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1, f.querySelector('[data-atc]'), extra);
    });
  });

  // qty steppers (non-drawer)
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-qty]');
    if (!b) return;
    var input = b.parentNode.querySelector('input');
    if (!input) return;
    var min = parseInt(input.min || '1', 10);
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(b.getAttribute('data-qty'), 10));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  if (drawer) getCart().then(renderCart).catch(function () {});

  /* ============================================================
     PRODUCT PAGE
     ============================================================ */

  // gallery
  (function gallery() {
    var main = $('[data-gallery]');
    if (!main) return;
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-thumb]');
      if (!t) return;
      var id = t.getAttribute('data-thumb');
      $$('[data-gallery] img').forEach(function (im) { im.classList.toggle('is-active', im.getAttribute('data-media') === id); });
      $$('[data-thumb]').forEach(function (b) { b.classList.toggle('is-active', b === t); });
    });
    window.lumenGallery = function (id) {
      var img = $('[data-gallery] img[data-media="' + id + '"]');
      if (!img) return;
      $$('[data-gallery] img').forEach(function (im) { im.classList.remove('is-active'); });
      img.classList.add('is-active');
      $$('[data-thumb]').forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-thumb') === String(id)); });
    };
  })();

  // variants
  (function variants() {
    var root = $('[data-variants]');
    if (!root) return;
    var data = JSON.parse($('[data-variant-json]').textContent);
    var idInput = root.querySelector('[name="id"]');
    var priceEl = $('[data-price]');
    var wasEl = $('[data-was]');
    var saveEl = $('[data-save]');
    var atc = $('[data-atc]');
    var barPrice = $('[data-bar-price]');
    var barBtn = $('[data-buybar] [data-quick-add]');
    var stEl = $('[data-sale-timer]');

    var chosen = [];
    $$('[data-opt]', root).forEach(function (g) {
      var i = parseInt(g.getAttribute('data-opt'), 10);
      var on = g.querySelector('.opt__val.is-on');
      chosen[i] = on ? on.getAttribute('data-val') : null;
    });

    function find() {
      return data.find(function (v) {
        return v.options.every(function (o, i) { return o === chosen[i]; });
      });
    }

    function apply() {
      var v = find();
      if (!v) {
        if (atc) { atc.classList.add('is-disabled'); atc.textContent = 'Unavailable'; }
        if (barBtn) barBtn.classList.add('is-disabled');
        return;
      }
      // the sticky pill adds whatever is selected, not the variant it rendered with
      if (barBtn) { barBtn.setAttribute('data-quick-add', v.id); barBtn.classList.toggle('is-disabled', !v.available); }
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.textContent = money(v.price);
      if (barPrice) barPrice.textContent = money(v.price);
      var sale = v.compare_at_price && v.compare_at_price > v.price;
      if (wasEl) { wasEl.textContent = sale ? money(v.compare_at_price) : ''; wasEl.hidden = !sale; }
      if (saveEl) {
        if (sale) { saveEl.textContent = '-' + Math.round((1 - v.price / v.compare_at_price) * 100) + '%'; saveEl.hidden = false; }
        else saveEl.hidden = true;
      }
      // the countdown belongs to the discount, so it follows the variant
      if (stEl && !stEl.hasAttribute('data-st-dead')) stEl.hidden = !sale;
      // let stock / other modules react to the new variant
      document.dispatchEvent(new CustomEvent('lumen:variant', { detail: v }));
      if (atc) {
        atc.classList.toggle('is-disabled', !v.available);
        atc.textContent = v.available ? (atc.getAttribute('data-label') || 'Add to cart') : 'Sold out';
      }
      if (v.featured_media && window.lumenGallery) window.lumenGallery(v.featured_media.id);
      if (history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        history.replaceState(null, '', u.toString());
      }
    }

    $$('[data-opt]', root).forEach(function (g) {
      var i = parseInt(g.getAttribute('data-opt'), 10);
      var out = g.querySelector('[data-opt-out]');
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.opt__val');
        if (!b) return;
        $$('.opt__val', g).forEach(function (x) { x.classList.remove('is-on'); });
        b.classList.add('is-on');
        chosen[i] = b.getAttribute('data-val');
        if (out) out.textContent = chosen[i];
        apply();
      });
    });
  })();

  // sticky buy bar
  (function buybar() {
    var bar = $('[data-buybar]');
    var anchor = $('[data-buy-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (en) {
      bar.classList.toggle('is-on', !en[0].isIntersecting && en[0].boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(anchor);
  })();

  /* ============================================================
     3D EFFECTS
     CSS transforms + a small canvas. No libraries, GPU-only
     properties, and everything yields to prefers-reduced-motion.
     ============================================================ */

  var FX = CFG.fx || {};
  // "Faster product pages" — the body carries .perf-lite on product pages when
  // the merchant opts into speed over flourish. The heavy canvas + tilt bail out.
  var liteMode = document.body.classList.contains('perf-lite');

  /* --- Falling rose petals --------------------------------- */
  (function petals() {
    if (!FX.petals || reduce || liteMode) return;

    var canvas = document.createElement('canvas');
    canvas.className = 'fx-petals';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = 0, H = 0, parts = [], raf = null, running = true;

    var colors = FX.petalColors || ['#e08a8a', '#c14e63'];
    var alpha = typeof FX.petalOpacity === 'number' ? FX.petalOpacity : 0.55;
    var COUNT = Math.max(6, Math.min(80, FX.petalCount || 30));

    function resize() {
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function make(seed) {
      return {
        x: Math.random() * W,
        y: seed ? Math.random() * H : -20 - Math.random() * H * 0.4,
        z: 0.35 + Math.random() * 0.9,          // depth → size + speed
        r: 6 + Math.random() * 8,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.02,
        spin: Math.random() * Math.PI * 2,       // the 3D turn
        vspin: 0.012 + Math.random() * 0.022,
        sway: 0.4 + Math.random() * 1.1,
        phase: Math.random() * Math.PI * 2,
        c: colors[(Math.random() * colors.length) | 0]
      };
    }

    function reset() { parts = []; for (var i = 0; i < COUNT; i++) parts.push(make(true)); }

    function draw(p) {
      // scaleX by cos(spin) fakes the petal rotating about its own axis in 3D
      var turn = Math.cos(p.spin);
      var w = p.r * p.z * Math.abs(turn);
      var h = p.r * p.z * 1.5;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = alpha * (0.45 + p.z * 0.55);
      ctx.fillStyle = p.c;
      ctx.beginPath();
      // a soft petal: two mirrored curves meeting at a point
      ctx.moveTo(0, -h / 2);
      ctx.bezierCurveTo(w, -h / 4, w, h / 3, 0, h / 2);
      ctx.bezierCurveTo(-w, h / 3, -w, -h / 4, 0, -h / 2);
      ctx.closePath();
      ctx.fill();
      // the lit edge as it turns
      if (turn > 0) {
        ctx.globalAlpha *= 0.4;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.ellipse(0, 0, Math.max(0.6, w * 0.32), h * 0.30, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    function tick() {
      if (!running) return;
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.phase += 0.01;
        p.y += (0.28 + p.z * 0.85);
        p.x += Math.sin(p.phase) * p.sway * p.z;
        p.rot += p.vr;
        p.spin += p.vspin;
        if (p.y - 40 > H) parts[i] = make(false);
        draw(p);
      }
      raf = requestAnimationFrame(tick);
    }

    function start() { if (!raf) { running = true; raf = requestAnimationFrame(tick); } }
    function stop() { running = false; if (raf) { cancelAnimationFrame(raf); raf = null; } }

    resize(); reset();

    // Don't compete with the intro for frames — the canvas was stealing
    // paint budget from the animation and making it look sluggish.
    var intro = document.getElementById('intro');
    if (intro) {
      var delay = (parseInt((CFG.intro || {}).duration, 10) || 4000) + 400;
      setTimeout(start, delay);
    } else {
      start();
    }

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { resize(); reset(); }, 200);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
  })();

  /* --- 3D tilt toward the cursor --------------------------- */
  (function tilt() {
    if (!FX.tilt || reduce || liteMode) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var MAX = FX.tiltStrength || 8;

    document.addEventListener('pointermove', function (e) {
      var el = e.target.closest('[data-tilt]');
      if (!el) return;
      var r = el.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--ry', (px * MAX).toFixed(2) + 'deg');
      el.style.setProperty('--rx', (-py * MAX).toFixed(2) + 'deg');
      el.style.setProperty('--gx', ((px + 0.5) * 100).toFixed(1) + '%');
      el.style.setProperty('--gy', ((py + 0.5) * 100).toFixed(1) + '%');
    }, { passive: true });

    document.addEventListener('pointerout', function (e) {
      var el = e.target.closest('[data-tilt]');
      if (!el || el.contains(e.relatedTarget)) return;
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    }, { passive: true });
  })();

  /* --- Hero glow follows the cursor (parallax depth) ------- */
  (function heroDepth() {
    if (!FX.heroDepth || reduce) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var hero = $('.hero');
    if (!hero) return;
    var orbs = $$('.hero__orb', hero);
    if (!orbs.length) return;

    var tx = 0, ty = 0, cx = 0, cy = 0, raf = null;

    hero.addEventListener('pointermove', function (e) {
      var r = hero.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });

    hero.addEventListener('pointerleave', function () { tx = 0; ty = 0; });

    function loop() {
      cx += (tx - cx) * 0.06;
      cy += (ty - cy) * 0.06;
      orbs.forEach(function (o, i) {
        var d = (i + 1) * 14;                    // each orb sits at a different depth
        o.style.transform = 'translate3d(' + (cx * d).toFixed(1) + 'px,' + (cy * d).toFixed(1) + 'px,0)';
      });
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) raf = requestAnimationFrame(loop);
      else raf = null;
    }
  })();

  /* ---------- BEFORE / AFTER SLIDER ---------- */
  (function beforeAfter() {
    $$('[data-ba]').forEach(function (ba) {
      var range = $('.ba__range', ba);
      if (!range) return;

      function apply() {
        ba.style.setProperty('--pos', range.value + '%');
      }

      range.addEventListener('input', apply, { passive: true });
      apply();

      // nudge it open a little on first view so it reads as draggable
      if (!reduce && 'IntersectionObserver' in window) {
        var teased = false;
        var io = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting || teased) return;
          teased = true;
          io.disconnect();

          var start = parseFloat(range.value);
          var t0 = null;
          function tease(ts) {
            if (!t0) t0 = ts;
            var p = Math.min((ts - t0) / 1400, 1);
            var ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
            // swing out and settle back
            range.value = start + Math.sin(ease * Math.PI) * 14;
            apply();
            if (p < 1) requestAnimationFrame(tease);
            else { range.value = start; apply(); }
          }
          setTimeout(function () { requestAnimationFrame(tease); }, 350);
        }, { threshold: 0.45 });
        io.observe(ba);
      }
    });
  })();

  /* ---------- COUNTDOWN ---------- */
  (function countdown() {
    $$('[data-countdown]').forEach(function (el) {
      var raw = (el.getAttribute('data-deadline') || '').trim();
      // "YYYY-MM-DD HH:MM" — Safari needs the T separator
      var end = new Date(raw.replace(' ', 'T')).getTime();
      if (isNaN(end)) return;

      var clock = $('[data-cd-clock]', el);
      var over = $('[data-cd-over]', el);
      var out = {
        days: $('[data-cd="days"]', el), hours: $('[data-cd="hours"]', el),
        mins: $('[data-cd="mins"]', el), secs: $('[data-cd="secs"]', el)
      };
      var timer;

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          clearInterval(timer);
          if (clock) clock.hidden = true;
          if (over) over.hidden = false;
          return;
        }
        var s = Math.floor(left / 1000);
        if (out.days) out.days.textContent = pad(Math.floor(s / 86400));
        if (out.hours) out.hours.textContent = pad(Math.floor(s % 86400 / 3600));
        if (out.mins) out.mins.textContent = pad(Math.floor(s % 3600 / 60));
        if (out.secs) out.secs.textContent = pad(s % 60);
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  })();

  /* ---------- SALE COUNTDOWN (product page) ----------
     Anchored to the STORE's clock, not the visitor's, so a customer in
     Tokyo and one in Los Angeles count down to the same instant.        */
  (function saleTimer() {
    $$('[data-sale-timer]').forEach(function (el) {
      var raw = (el.getAttribute('data-offset') || '').trim();
      // Liquid gives "+0100"; the JS date parser needs "+01:00".
      var off = /^[+-]\d{4}$/.test(raw) ? raw.slice(0, 3) + ':' + raw.slice(3) : 'Z';

      function midnightTonight() {
        var today = (el.getAttribute('data-today') || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(today)) return NaN;
        // Roll the calendar date forward in UTC — pure date arithmetic, no
        // timezone involved — then pin it to the store's offset. (On the two
        // clock-change nights a year this can land an hour out.)
        var d = new Date(today + 'T00:00:00Z');
        d.setUTCDate(d.getUTCDate() + 1);
        return Date.parse(d.toISOString().slice(0, 10) + 'T00:00:00' + off);
      }

      function fixedEnd() {
        var s = (el.getAttribute('data-ends') || '').trim().replace(' ', 'T');
        if (!s) return NaN;
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s += 'T23:59:59';       // date only → end of day
        if (!/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += off;            // merchant typed store time
        return Date.parse(s);
      }

      var end = el.getAttribute('data-mode') === 'date' ? fixedEnd() : NaN;
      if (isNaN(end)) end = midnightTonight();
      // Never show a broken clock — mark it dead so the variant switcher
      // doesn't reveal it again.
      if (isNaN(end)) { el.hidden = true; el.setAttribute('data-st-dead', ''); return; }

      var clock = $('[data-st-clock]', el);
      var lbl = $('.saletimer__lbl', el);
      var out = {
        days: $('[data-st="days"]', el), hours: $('[data-st="hours"]', el),
        mins: $('[data-st="mins"]', el), secs: $('[data-st="secs"]', el)
      };
      var timer;

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          clearInterval(timer);
          el.classList.add('is-over');
          if (clock) clock.hidden = true;
          if (lbl) lbl.textContent = el.getAttribute('data-ended-text') || '';
          return;
        }
        var s = Math.floor(left / 1000);
        var d = Math.floor(s / 86400);
        if (out.days) {
          out.days.hidden = d === 0;
          out.days.textContent = d ? d + (S.daysShort || 'd') + ' ' : '';
        }
        if (out.hours) out.hours.textContent = pad(Math.floor(s % 86400 / 3600));
        if (out.mins) out.mins.textContent = pad(Math.floor(s % 3600 / 60));
        if (out.secs) out.secs.textContent = pad(s % 60);
      }
      tick();
      timer = setInterval(tick, 1000);
    });
  })();

  /* ---------- LAZY VIDEO EMBED ---------- */
  (function videoEmbed() {
    $$('[data-video-embed]').forEach(function (box) {
      var btn = $('.vid__play', box);
      if (!btn) return;
      btn.addEventListener('click', function () {
        var src = box.getAttribute('data-src') || '';
        var embed = src;
        var yt = src.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
        var vm = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        if (yt) embed = 'https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0';
        else if (vm) embed = 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=1';

        var f = document.createElement('iframe');
        f.src = embed;
        f.title = 'Video';
        f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture';
        f.allowFullscreen = true;
        f.loading = 'lazy';
        box.innerHTML = '';
        box.appendChild(f);
      });
    });
  })();

  /* ---------- RECENTLY VIEWED ---------- */
  (function recentlyViewed() {
    var KEY = 'lumen_rv';
    function read() {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    }
    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 12))); } catch (e) {}
    }

    // record the product being viewed
    var p = window.LumenProduct;
    if (p && p.handle) {
      var list = read().filter(function (x) { return x.handle !== p.handle; });
      list.unshift(p);
      write(list);
    }

    var sec = $('[data-recently-viewed]');
    if (!sec) return;
    var grid = $('[data-rv-grid]', sec);
    var items = read().filter(function (x) { return !p || x.handle !== p.handle; });
    if (!items.length) return;

    var max = 4;
    items = items.slice(0, max);

    grid.innerHTML = items.map(function (it) {
      return '<div class="pcard">' +
        '<a class="pcard__media" href="' + esc(it.url) + '" data-tilt>' +
          (it.image ? '<img src="' + esc(it.image) + '" alt="' + esc(it.title) + '" loading="lazy">' : '') +
        '</a>' +
        '<div class="pcard__info">' +
          '<a class="pcard__title" href="' + esc(it.url) + '">' + esc(it.title) + '</a>' +
          '<span class="pcard__price">' + esc(it.price) + '</span>' +
        '</div>' +
      '</div>';
    }).join('');
    sec.hidden = false;

    var clear = $('[data-rv-clear]', sec);
    if (clear) clear.addEventListener('click', function () {
      try { localStorage.removeItem(KEY); } catch (e) {}
      sec.hidden = true;
    });
  })();

  /* ---------- COLLECTION SORT ---------- */
  (function sort() {
    var sel = $('[data-sort]');
    if (!sel) return;
    sel.addEventListener('change', function () {
      var u = new URL(window.location.href);
      u.searchParams.set('sort_by', sel.value);
      u.searchParams.delete('page');
      window.location.href = u.toString();
    });
  })();

  /* ---------- FREQUENTLY BOUGHT TOGETHER ---------- */
  (function fbt() {
    // may appear twice: compact under Add to cart, and the full-width section
    $$('[data-fbt]').forEach(setup);

    function setup(root) {
      var totalEl = $('[data-fbt-total]', root);
      var addBtn = $('[data-fbt-add]', root);
      var checks = $$('[data-fbt-check]', root);

      function recalc() {
        var cents = 0;
        checks.forEach(function (c) { if (c.checked) cents += parseInt(c.getAttribute('data-price'), 10) || 0; });
        if (totalEl) totalEl.textContent = money(cents);
        if (addBtn) addBtn.disabled = cents === 0;
      }
      checks.forEach(function (c) { c.addEventListener('change', recalc); });
      recalc();

      if (addBtn) addBtn.addEventListener('click', function () {
        var items = checks.filter(function (c) { return c.checked; })
          .map(function (c) { return { id: parseInt(c.getAttribute('data-id'), 10), quantity: 1 }; });
        if (!items.length) return;
        addBtn.classList.add('is-busy');
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
          .then(getCart)
          .then(function (c) { renderCart(c); toast(S.added || 'Added to cart'); if (CFG.cartDrawer) openDrawer(true); })
          .catch(function () { toast(S.error || 'Something went wrong', 'err'); })
          .then(function () { addBtn.classList.remove('is-busy'); });
      });
    }
  })();

  /* ---------- COUNTRY / CURRENCY SELECTOR ---------- */
  (function localization() {
    var sel = $('[data-loc-country]');
    if (!sel) return;
    sel.addEventListener('change', function () {
      var f = sel.closest('form');
      if (f) f.submit();
    });
  })();

  /* ---------- STOCK COUNTER + LIVE VIEWERS ---------- */
  (function productMeta() {
    var stock = $('[data-stock]');
    var viewers = $('[data-viewers]');
    if (!stock && !viewers) return;

    // current variant, either from the variant switcher or the id in the form
    var variants = [];
    var vjson = $('[data-variant-json]');
    if (vjson) { try { variants = JSON.parse(vjson.textContent); } catch (e) {} }

    function currentVariant() {
      var idEl = $('[name="id"]');
      var id = idEl ? parseInt(idEl.value, 10) : 0;
      return variants.filter(function (v) { return v.id === id; })[0] || variants[0];
    }

    function renderStock(v) {
      if (!stock || !v) return;
      var txt = $('[data-stock-text]', stock);
      var managed = v.inventory_management;               // null when not tracked
      var qty = v.inventory_quantity;
      var thr = parseInt(stock.getAttribute('data-threshold'), 10) || 10;
      // only speak when we actually know the number and it's genuinely low
      if (v.available && managed && typeof qty === 'number' && qty > 0 && qty <= thr) {
        var one = (S.stockOne || 'Only __N__ left in stock');
        var many = (S.stockMany || 'Only __N__ left in stock');
        txt.textContent = (qty === 1 ? one : many).replace('__N__', qty);
        stock.classList.toggle('is-critical', qty <= Math.max(1, Math.ceil(thr / 3)));
        stock.hidden = false;
      } else {
        stock.hidden = true;
      }
    }

    renderStock(currentVariant());
    document.addEventListener('lumen:variant', function (e) { renderStock(e.detail); });

    // simulated viewers — clearly optional, off by default
    if (viewers) {
      var vt = $('[data-viewers-text]', viewers);
      var lo = parseInt(viewers.getAttribute('data-min'), 10) || 8;
      var hi = parseInt(viewers.getAttribute('data-max'), 10) || 24;
      if (hi < lo) hi = lo;
      function rnd() { return lo + Math.floor(Math.random() * (hi - lo + 1)); }
      function show(n) {
        vt.textContent = (S.viewers || '__N__ people are viewing this right now').replace('__N__', n);
      }
      show(rnd());
      viewers.hidden = false;
      setInterval(function () {
        // drift by ±1 so it feels alive rather than random
        var cur = parseInt((vt.textContent.match(/\d+/) || [rnd()])[0], 10);
        var next = Math.min(hi, Math.max(lo, cur + (Math.random() < 0.5 ? -1 : 1)));
        show(next);
      }, 6000 + Math.random() * 5000);
    }
  })();

  /* ---------- ESTIMATED DELIVERY DATE ---------- */
  (function deliveryDate() {
    var el = $('[data-delivery]');
    if (!el) return;
    var out = $('[data-delivery-range]', el);
    if (!out) return;

    var proc = parseInt(el.getAttribute('data-processing'), 10) || 0;
    var min = parseInt(el.getAttribute('data-min'), 10) || 0;
    var max = parseInt(el.getAttribute('data-max'), 10) || 0;
    var cutoff = parseInt(el.getAttribute('data-cutoff'), 10);
    var skipWk = el.getAttribute('data-skip-weekends') === 'true';

    var now = new Date();
    // ordered after the cut-off? handling starts tomorrow
    var startOffset = (!isNaN(cutoff) && now.getHours() >= cutoff) ? 1 : 0;

    function addDays(base, n) {
      var d = new Date(base.getTime());
      var added = 0;
      // move the handling+shipping days forward, optionally skipping Sat/Sun
      while (added < n) {
        d.setDate(d.getDate() + 1);
        var day = d.getDay();
        if (skipWk && (day === 0 || day === 6)) continue;
        added++;
      }
      return d;
    }

    var base = new Date(now.getTime());
    if (startOffset) base.setDate(base.getDate() + 1);

    var lead = proc + min;
    var lag = proc + max;
    var d1 = addDays(base, lead);
    var d2 = addDays(base, lag);

    var loc = (CFG.locale || 'en');
    function fmt(d, withMonth) {
      try { return new Intl.DateTimeFormat(loc, withMonth ? { weekday: 'short', day: 'numeric', month: 'short' } : { weekday: 'short', day: 'numeric' }).format(d); }
      catch (e) { return d.toDateString(); }
    }

    var sameMonth = d1.getMonth() === d2.getMonth();
    out.textContent = (lead === lag)
      ? fmt(d2, true)
      : fmt(d1, !sameMonth) + ' – ' + fmt(d2, true);
  })();

  /* ---------- QUANTITY BREAK TIERS ---------- */
  (function tiers() {
    var wrap = $('[data-tiers]');
    if (!wrap) return;
    var qtyInput = $('form[data-product-form] [name="quantity"]');
    wrap.addEventListener('click', function (e) {
      var t = e.target.closest('[data-tier-qty]');
      if (!t) return;
      $$('[data-tier-qty]', wrap).forEach(function (x) { x.classList.remove('is-on'); });
      t.classList.add('is-on');
      if (qtyInput) {
        qtyInput.value = Math.max(1, parseInt(t.getAttribute('data-tier-qty'), 10) || 1);
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  })();

  /* ---------- SIZE GUIDE MODAL ---------- */
  (function sizeGuide() {
    var modal = $('[data-sizeguide-modal]');
    if (!modal) return;
    function open(v) {
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.toggle('is-open', v); });
      document.body.style.overflow = v ? 'hidden' : '';
      if (!v) setTimeout(function () { modal.hidden = true; }, 300);
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-sizeguide-open]')) { e.preventDefault(); open(true); }
      if (e.target.closest('[data-sizeguide-close]')) open(false);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !modal.hidden) open(false); });
  })();

  /* ============================================================
     WISHLIST  (localStorage, no account needed)
     ============================================================ */
  var WISH = (function wishlist() {
    var KEY = 'lumen_wish';
    function read() { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } }
    function write(l) { try { localStorage.setItem(KEY, JSON.stringify(l.slice(0, 60))); } catch (e) {} }
    function has(handle) { return read().some(function (x) { return x.handle === handle; }); }

    function toggle(item) {
      var l = read();
      var i = l.map(function (x) { return x.handle; }).indexOf(item.handle);
      var added;
      if (i >= 0) { l.splice(i, 1); added = false; }
      else { l.unshift(item); added = true; }
      write(l); paint(); renderPanel();
      return added;
    }

    function count() { return read().length; }

    function paint() {
      $$('[data-wishlist]').forEach(function (b) {
        var on = has(b.getAttribute('data-handle'));
        b.classList.toggle('is-saved', on);
        b.setAttribute('aria-pressed', String(on));
      });
      $$('[data-wish-count]').forEach(function (n) {
        n.textContent = count();
        n.hidden = count() === 0;
      });
    }

    function itemFrom(btn) {
      return {
        handle: btn.getAttribute('data-handle'),
        title: btn.getAttribute('data-title'),
        url: btn.getAttribute('data-url'),
        image: btn.getAttribute('data-image'),
        price: btn.getAttribute('data-price')
      };
    }

    // toggle buttons (cards + PDP)
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-wishlist]');
      if (!b) return;
      e.preventDefault();
      var added = toggle(itemFrom(b));
      toast(added ? (S.wishAdded || 'Saved to wishlist') : (S.wishRemoved || 'Removed from wishlist'));
      if (added && !$('[data-wish-open]')) { /* no header entry, still fine */ }
    });

    // slide-out panel
    var panel = $('[data-wish-panel]');
    var overlay = $('[data-wish-overlay]');
    function openPanel(v) {
      if (!panel) return;
      panel.classList.toggle('is-open', v);
      panel.setAttribute('aria-hidden', String(!v));
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(function () { overlay.classList.toggle('is-open', v); });
        if (!v) setTimeout(function () { overlay.hidden = true; }, 350);
      }
      document.body.style.overflow = v ? 'hidden' : '';
      if (v) renderPanel();
    }
    function renderPanel() {
      if (!panel) return;
      var grid = $('[data-wish-grid]', panel);
      var empty = $('[data-wish-empty]', panel);
      var items = read();
      if (!grid) return;
      if (!items.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;
      grid.innerHTML = items.map(function (it) {
        return '<div class="wish__item">' +
          '<a class="wish__img" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(it.image) + '" alt="" loading="lazy">' : '') + '</a>' +
          '<div class="wish__meta">' +
            '<a class="wish__title" href="' + esc(it.url) + '">' + esc(it.title) + '</a>' +
            '<span class="wish__price">' + esc(it.price || '') + '</span>' +
            '<div class="wish__actions">' +
              '<a class="btn btn--sm btn--primary" href="' + esc(it.url) + '">' + esc(S.wishView || 'View') + '</a>' +
              '<button type="button" class="wish__rm" data-wish-remove="' + esc(it.handle) + '">' + esc(S.remove || 'Remove') + '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      }).join('');
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-wish-open]')) { e.preventDefault(); openPanel(true); }
      if (e.target.closest('[data-wish-close]')) openPanel(false);
      var rm = e.target.closest('[data-wish-remove]');
      if (rm) {
        var h = rm.getAttribute('data-wish-remove');
        write(read().filter(function (x) { return x.handle !== h; }));
        paint(); renderPanel();
      }
    });
    if (overlay) overlay.addEventListener('click', function () { openPanel(false); });

    paint();
    return { paint: paint, count: count };
  })();

  /* ============================================================
     QUICK VIEW  (fetch product JSON, build a mini buy box)
     ============================================================ */
  (function quickView() {
    var modal = $('[data-qv-modal]');
    if (!modal) return;
    var box = $('[data-qv-box]', modal);

    function open(v) {
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.toggle('is-open', v); });
      document.body.style.overflow = v ? 'hidden' : '';
      if (!v) setTimeout(function () { modal.hidden = true; if (box) box.innerHTML = ''; }, 300);
    }

    function render(p) {
      var chosen = (p.variants.filter(function (v) { return v.available; })[0] || p.variants[0]);
      var opts = '';
      if (!(p.variants.length === 1 && p.variants[0].title === 'Default Title')) {
        opts = p.options.map(function (name, oi) {
          var values = [];
          p.variants.forEach(function (v) { if (values.indexOf(v.options[oi]) < 0) values.push(v.options[oi]); });
          return '<div class="qv__opt" data-qv-opt="' + oi + '">' +
            '<div class="qv__optname">' + esc(name) + '</div>' +
            '<div class="qv__vals">' + values.map(function (val) {
              var on = chosen.options[oi] === val;
              return '<button type="button" class="qv__val' + (on ? ' is-on' : '') + '" data-qv-val="' + esc(val) + '">' + esc(val) + '</button>';
            }).join('') + '</div></div>';
        }).join('');
      }
      var img = (p.featured_image || (p.media && p.media[0] && p.media[0].preview_image && p.media[0].preview_image.src) || '');
      if (img && img.indexOf('//') === 0) img = 'https:' + img;
      if (img) img += (img.indexOf('?') >= 0 ? '&' : '?') + 'width=700';
      box.innerHTML =
        '<div class="qv__media">' + (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '">' : '') + '</div>' +
        '<div class="qv__info">' +
          '<h3 class="qv__title">' + esc(p.title) + '</h3>' +
          '<div class="qv__price" data-qv-price>' + money(chosen.price) + '</div>' +
          opts +
          '<button type="button" class="btn btn--primary btn--block" data-qv-add>' + esc(S.add || 'Add to cart') + '</button>' +
          '<a class="qv__full" href="/products/' + esc(p.handle) + '">' + esc(S.qvFull || 'View full details') + '</a>' +
        '</div>';

      var chosenOpts = chosen.options.slice();
      function match() {
        return p.variants.filter(function (v) {
          return v.options.every(function (o, i) { return o === chosenOpts[i]; });
        })[0];
      }
      function refresh() {
        var v = match();
        var priceEl = $('[data-qv-price]', box);
        var addBtn = $('[data-qv-add]', box);
        if (v) {
          if (priceEl) priceEl.textContent = money(v.price);
          if (addBtn) { addBtn.disabled = !v.available; addBtn.textContent = v.available ? (S.add || 'Add to cart') : (S.soldOut || 'Sold out'); addBtn.setAttribute('data-id', v.id); }
        }
      }
      refresh();
      $$('[data-qv-opt]', box).forEach(function (g) {
        var oi = parseInt(g.getAttribute('data-qv-opt'), 10);
        g.addEventListener('click', function (e) {
          var b = e.target.closest('[data-qv-val]');
          if (!b) return;
          $$('[data-qv-val]', g).forEach(function (x) { x.classList.remove('is-on'); });
          b.classList.add('is-on');
          chosenOpts[oi] = b.getAttribute('data-qv-val');
          refresh();
        });
      });
      var addBtn = $('[data-qv-add]', box);
      if (addBtn) addBtn.addEventListener('click', function () {
        var v = match();
        if (!v || !v.available) return;
        addToCart(v.id, 1, addBtn).then(function () { open(false); });
      });
    }

    document.addEventListener('click', function (e) {
      var trig = e.target.closest('[data-quick-view]');
      if (trig) {
        e.preventDefault();
        var handle = trig.getAttribute('data-quick-view');
        open(true);
        box.innerHTML = '<div class="qv__loading">' + esc(S.loading || 'Loading…') + '</div>';
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.json(); })
          .then(render)
          .catch(function () { box.innerHTML = '<div class="qv__loading">' + esc(S.error || 'Something went wrong') + '</div>'; });
      }
      if (e.target.closest('[data-qv-close]')) open(false);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !modal.hidden) open(false); });
  })();

  /* ============================================================
     PREDICTIVE SEARCH  (Shopify suggest API)
     ============================================================ */
  (function predictiveSearch() {
    var wrap = $('[data-search]');
    if (!wrap) return;
    var overlay = $('[data-search-overlay]');
    var input = $('[data-search-input]', wrap);
    var results = $('[data-search-results]', wrap);
    if (!input || !results) return;

    function open(v) {
      wrap.classList.toggle('is-open', v);
      wrap.setAttribute('aria-hidden', String(!v));
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(function () { overlay.classList.toggle('is-open', v); });
        if (!v) setTimeout(function () { overlay.hidden = true; }, 300);
      }
      document.body.style.overflow = v ? 'hidden' : '';
      if (v) setTimeout(function () { input.focus(); }, 60);
    }

    var t;
    function query(q) {
      if (!q || q.length < 2) { results.innerHTML = ''; results.classList.remove('has-results'); return; }
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url).then(function (r) { return r.json(); }).then(function (data) {
        var prods = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!prods.length) {
          results.innerHTML = '<p class="search__none">' + esc((S.searchNone || 'No matches for “__Q__”').replace('__Q__', q)) + '</p>';
          results.classList.add('has-results');
          return;
        }
        results.innerHTML = prods.map(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
          return '<a class="search__row" href="' + esc(p.url) + '">' +
            '<span class="search__thumb">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '') + '</span>' +
            '<span class="search__meta"><span class="search__title">' + esc(p.title) + '</span>' +
            (p.price ? '<span class="search__price">' + esc(p.price) + '</span>' : '') + '</span></a>';
        }).join('') +
        '<a class="search__all" href="/search?q=' + encodeURIComponent(q) + '">' + esc((S.searchAll || 'See all results')) + '</a>';
        results.classList.add('has-results');
      }).catch(function () {});
    }

    input.addEventListener('input', function () { clearTimeout(t); var q = input.value.trim(); t = setTimeout(function () { query(q); }, 220); });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-open]')) { e.preventDefault(); open(true); }
      if (e.target.closest('[data-search-close]')) open(false);
    });
    if (overlay) overlay.addEventListener('click', function () { open(false); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && wrap.classList.contains('is-open')) open(false); });
  })();

})();
